/**
 * Board Notes API service - handles board note API calls
 */

import { apiClient } from './client';

// =============================================================================
// Types
// =============================================================================

export interface BoardNoteAuthor {
  id: string;
  displayName: string;
  avatarUrl?: string;
}

export interface BoardNote {
  id: string;
  boardId: string;
  title: string;
  content?: string;
  position: number;
  createdById?: string;
  updatedById?: string;
  createdBy?: BoardNoteAuthor;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBoardNoteRequest {
  title: string;
  content?: string;
}

export interface UpdateBoardNoteRequest {
  title?: string;
  content?: string;
}

// =============================================================================
// Board Notes API
// =============================================================================

export const boardNotesApi = {
  /**
   * Get all notes for a board
   */
  list: async (boardId: string): Promise<BoardNote[]> => {
    const response = await apiClient.get<unknown[]>(`/boards/${boardId}/notes`);
    return response.data.map(transformBoardNote);
  },

  /**
   * Get a specific board note
   */
  get: async (boardId: string, noteId: string): Promise<BoardNote> => {
    const response = await apiClient.get<unknown>(`/boards/${boardId}/notes/${noteId}`);
    return transformBoardNote(response.data);
  },

  /**
   * Create a new note on a board
   */
  create: async (boardId: string, data: CreateBoardNoteRequest): Promise<BoardNote> => {
    const response = await apiClient.post<unknown>(`/boards/${boardId}/notes`, {
      title: data.title,
      content: data.content,
    });
    return transformBoardNote(response.data);
  },

  /**
   * Update a board note
   */
  update: async (
    boardId: string,
    noteId: string,
    data: UpdateBoardNoteRequest
  ): Promise<BoardNote> => {
    const response = await apiClient.patch<unknown>(`/boards/${boardId}/notes/${noteId}`, {
      title: data.title,
      content: data.content,
    });
    return transformBoardNote(response.data);
  },

  /**
   * Delete a board note
   */
  delete: async (boardId: string, noteId: string): Promise<void> => {
    await apiClient.delete(`/boards/${boardId}/notes/${noteId}`);
  },

  /**
   * Reorder board notes
   */
  reorder: async (boardId: string, noteIds: string[]): Promise<BoardNote[]> => {
    const response = await apiClient.post<unknown[]>(`/boards/${boardId}/notes/reorder`, {
      note_ids: noteIds,
    });
    return response.data.map(transformBoardNote);
  },
};

// =============================================================================
// Transform helpers
// =============================================================================

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
function transformBoardNoteAuthor(data: any): BoardNoteAuthor | undefined {
  if (!data) return undefined;
  return {
    id: data.id,
    displayName: data.displayName ?? data.display_name,
    avatarUrl: data.avatarUrl ?? data.avatar_url,
  };
}

function transformBoardNote(data: any): BoardNote {
  const note: BoardNote = {
    id: data.id,
    boardId: data.boardId ?? data.board_id,
    title: data.title,
    content: data.content,
    position: data.position,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
  const createdById = data.createdById ?? data.created_by_id;
  if (createdById) note.createdById = createdById;
  const updatedById = data.updatedById ?? data.updated_by_id;
  if (updatedById) note.updatedById = updatedById;
  const createdBy = transformBoardNoteAuthor(data.createdBy ?? data.created_by);
  if (createdBy) note.createdBy = createdBy;
  return note;
}
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */

export default boardNotesApi;
